import { ɵglobal } from '@angular/core';
import { fromEvent, race, Subject } from 'rxjs';
import {
  delay,
  filter,
  map,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { isArray } from './common';
import { isMobile } from './is-mobile';

function closest(element: HTMLElement, selector?: string): HTMLElement | null {
  if(!selector) {
    return null;
  }

  /*
   * In certain browsers (e.g. Edge 44.18362.449.0) HTMLDocument does
   * not support `Element.prototype.closest`. To emulate the correct behaviour
   * we return null when the method is missing.
   *
   * Note that in evergreen browsers `closest(document.documentElement, 'html')`
   * will return the document element whilst in Edge null will be returned. This
   * compromise was deemed good enough.
   */
  if(typeof element.closest === 'undefined') {
    return null;
  }

  return element.closest(selector);
}

export const isContainedIn = (element: HTMLElement, array?: HTMLElement[]) =>
  array ? array.some(item => item.contains(element)) : false;

const matchesSelectorIfAny = (element: HTMLElement, selector?: string) =>
  !selector || closest(element, selector) !== null;

// setting 'ngbAutoClose' synchronously on mobile results in immediate popup closing
// when tapping on the triggering element
const wrapAsyncForMobile = (fn: any) => isMobile ? () => setTimeout(() => fn(), 100) : fn;

export const enum SOURCE { ESCAPE, CLICK }

export type AutoCloseOptions = {
  type: boolean | 'inside' | 'outside',
  ignoreElements?: HTMLElement[],
  insideSelector?: string
};

export function autoClose(
  insideElements: HTMLElement|HTMLElement[],
  close: (source: SOURCE) => void,
  options: AutoCloseOptions = { type: 'outside' },
  document?: Document
) {
  const hasWindowObj = typeof window !== 'undefined';

  if(!hasWindowObj && !document) {
    return;
  } else if(hasWindowObj && !document) {
    document = window.document;
  }
  
  if(!isArray<HTMLElement>(insideElements)) {
    insideElements = [ insideElements ];
  }

  const zone = ɵglobal.Zone;
  const angularZone = zone.current!;
  const destroyer$ = new Subject<void>();

  // closing on ESC and outside clicks
  zone.root.run(wrapAsyncForMobile(() => {    
    const shouldCloseOnClick = (event: MouseEvent) => {
      const element = event.target as HTMLElement;

      if(event.button === 2 || isContainedIn(element, options.ignoreElements)) {
        return false;
      }

      if(options.type === 'inside') {
        return isContainedIn(element, insideElements as HTMLElement[]) && matchesSelectorIfAny(element, options.insideSelector);
      } else if (options.type === 'outside') {
        return !isContainedIn(element, insideElements as HTMLElement[]);
      }

      return matchesSelectorIfAny(element, options.insideSelector) || !isContainedIn(element, insideElements as HTMLElement[]);
    };

    const escapes$ = fromEvent<KeyboardEvent>(document!, 'keydown')
      .pipe(
        filter(e => e.key === 'Escape'),
        tap(e => e.preventDefault()),
        takeUntil(destroyer$)
      );

    // we have to pre-calculate 'shouldCloseOnClick' on 'mousedown',
    // because on 'mouseup' DOM nodes might be detached
    const mouseDowns$ =
        fromEvent<MouseEvent>(document!, 'mousedown')
          .pipe(
            map(shouldCloseOnClick),
            takeUntil(destroyer$)
          );

    const closeableClicks$ = fromEvent<MouseEvent>(document!, 'mouseup')
      .pipe(
        withLatestFrom(mouseDowns$),
        filter(([_, shouldClose]) => shouldClose),
        delay(0),
        takeUntil(destroyer$)
      );

    race([escapes$.pipe(map(_ => SOURCE.ESCAPE)), closeableClicks$.pipe(map(_ => SOURCE.CLICK))]).subscribe((source: SOURCE) => angularZone.run(() => close(source)));
  }));

  return destroyer$;
}